import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { Button, errorMessage, Icon, Input, isTemplatePlaceholder } from '@dataroom/shared';
import type { CategoryNode } from '@dataroom/shared';
import { categoryNodesById } from '@dataroom/shared';
import { PendingCategoryError, useCreateCategoryHere } from '@/api/add-category';
import {
  categoryNameProblem,
  cleanCategoryName,
  findSiblingByName,
  MAX_CATEGORY_NAME_LENGTH,
} from '@/lib/new-category';

/** What choosing the new (or same-named existing) category hands the caller. */
export interface ChosenCategory {
  id: string;
  name: string;
  hasChildren: boolean;
}

interface NewCategoryHereProps {
  roomId: string;
  categories: CategoryNode[];
  /** The level's parent; null = the top level. */
  parentId: string | null;
  /** The parent's name, for the field's label (null at the top level). */
  parentName: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Another action is in flight. */
  disabled: boolean;
  /** Show "N" as this row's keyboard shortcut (the review queue). */
  shortcut?: boolean;
  /** Choose it as this level's option — exactly as clicking an existing row. */
  onChosen: (category: ChosenCategory) => void;
}

/**
 * "+ New category here" — the last row of a filing level's list (owner ask
 * 2026-10-15: "at every level of classification, there should be an option to
 * create a new classification at the level it is in"). Clicking it (or N in
 * the review queue) opens an inline name field: Enter saves, Esc cancels. Save
 * creates the category under the level's parent (useCreateCategoryHere — the
 * existing folder import) and chooses it, so the filing continues exactly as if
 * an existing row was clicked. A name that already exists at this level just
 * chooses that one. The closed row holds no query hooks (the form, with its
 * mutation, mounts only while open).
 */
export function NewCategoryHere({
  open,
  onOpenChange,
  disabled,
  shortcut = false,
  ...form
}: NewCategoryHereProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const refocus = useRef(false);
  useEffect(() => {
    if (!open && refocus.current) {
      refocus.current = false;
      buttonRef.current?.focus();
    }
  }, [open]);

  if (open) {
    return (
      <NewCategoryForm
        {...form}
        disabled={disabled}
        onCancel={() => {
          refocus.current = true;
          onOpenChange(false);
        }}
        onDone={(category) => {
          onOpenChange(false);
          form.onChosen(category);
        }}
      />
    );
  }
  return (
    <button
      ref={buttonRef}
      type="button"
      disabled={disabled}
      onClick={() => onOpenChange(true)}
      aria-keyshortcuts={shortcut ? 'N' : undefined}
      className="mt-1.5 flex w-full items-center gap-3 rounded-md border border-dashed px-3 py-2 text-left text-ink-2 transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
    >
      <Icon name="plus" size={14} className="w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 font-medium">New category here</span>
      {shortcut ? (
        <kbd
          aria-hidden="true"
          className="rounded border bg-surface-2 px-1 font-sans text-[11px] font-semibold text-ink-2"
        >
          N
        </kbd>
      ) : null}
    </button>
  );
}

function NewCategoryForm({
  roomId,
  categories,
  parentId,
  parentName,
  disabled,
  onCancel,
  onDone,
}: Omit<NewCategoryHereProps, 'open' | 'onOpenChange' | 'shortcut' | 'onChosen'> & {
  onCancel: () => void;
  onDone: (category: ChosenCategory) => void;
}) {
  const create = useCreateCategoryHere(roomId);
  const nameId = useId();
  const hintId = useId();
  const [name, setName] = useState('');
  const [tried, setTried] = useState(false);
  const knownIds = useMemo(() => new Set(categoryNodesById(categories).keys()), [categories]);

  const clean = cleanCategoryName(name);
  const problem = categoryNameProblem(name);
  const existing = problem === null ? findSiblingByName(categories, parentId, clean) : null;
  const placeholder = problem === null && isTemplatePlaceholder(clean);
  const saving = create.isPending;

  const save = (e?: FormEvent) => {
    e?.preventDefault();
    setTried(true);
    if (disabled || saving || problem !== null || placeholder) return;
    if (existing) {
      onDone({ id: existing.id, name: existing.name, hasChildren: existing.children.length > 0 });
      return;
    }
    create.mutate(
      { parentId, name: clean, knownIds },
      { onSuccess: (c) => onDone({ id: c.id, name: c.name, hasChildren: false }) },
    );
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      // Cancel the field only — never the dialog around it, never a queue key.
      e.preventDefault();
      e.stopPropagation();
      if (!saving) onCancel();
    }
  };

  let hint: { text: string; error: boolean; fix?: string } | null = null;
  if (problem?.kind === 'empty') {
    if (tried) hint = { text: 'Type a name for the new category.', error: true };
  } else if (problem?.kind === 'account_number') {
    hint = {
      text: `That looks like a full account number. Use only the last 4 digits — for example “${problem.suggestion}”.`,
      error: true,
      fix: problem.suggestion,
    };
  } else if (problem?.kind === 'slash') {
    hint = {
      text: 'A name can’t contain “/” — it separates the levels of a category.',
      error: true,
    };
  } else if (problem?.kind === 'too_long') {
    hint = { text: `Keep the name under ${MAX_CATEGORY_NAME_LENGTH} characters.`, error: true };
  } else if (placeholder) {
    hint = {
      text: 'Square brackets mark a checklist placeholder — use a plain name.',
      error: true,
    };
  } else if (existing) {
    hint = { text: `“${existing.name}” is already here — saving chooses it.`, error: false };
  } else if (parentId === null) {
    hint = {
      text: 'A new top-level category is due-diligence material: buyers see it only by a grant.',
      error: false,
    };
  }

  const error = create.error
    ? create.error instanceof PendingCategoryError
      ? create.error.message
      : errorMessage(create.error)
    : null;

  return (
    <form
      className="mt-1.5 space-y-2 rounded-md border border-dashed p-3"
      aria-label="New category here"
      onSubmit={save}
      noValidate
    >
      <label htmlFor={nameId} className="block text-xs font-medium text-muted-foreground">
        {parentName ? `New category under ${parentName}` : 'New top-level category'}
      </label>
      <Input
        id={nameId}
        value={name}
        maxLength={MAX_CATEGORY_NAME_LENGTH + 20}
        placeholder="Name — Enter to save, Esc to cancel"
        autoFocus
        autoComplete="off"
        aria-invalid={hint?.error ? true : undefined}
        aria-describedby={hint ? hintId : undefined}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={saving}
      />
      {hint ? (
        <p
          id={hintId}
          className={hint.error ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}
          role={hint.error ? 'alert' : 'status'}
        >
          {hint.text}
          {hint.fix ? (
            <>
              {' '}
              <button
                type="button"
                className="font-semibold text-primary underline-offset-2 hover:underline"
                onClick={() => setName(hint.fix!)}
              >
                Use “{hint.fix}”
              </button>
            </>
          ) : null}
        </p>
      ) : null}
      {error ? (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={disabled || saving}>
          {saving ? 'Saving…' : existing ? 'Choose it' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
